import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 errors (unauthorized)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear invalid token
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userName');
      localStorage.removeItem('userPlan');
      localStorage.removeItem('userId');
      // Redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  signup: async (email, name, password) => {
    const response = await api.post('/api/auth/signup', {
      email,
      name,
      password,
    });
    return response.data;
  },

  login: async (email, password) => {
    const response = await api.post('/api/auth/login', {
      email,
      password,
    });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/api/auth/me');
    return response.data;
  },
};

// Documents API calls
export const documentsAPI = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/api/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getAll: async () => {
    const response = await api.get('/api/documents');
    return response.data;
  },

  getById: async (documentId) => {
    const response = await api.get(`/api/documents/${documentId}`);
    return response.data;
  },

  delete: async (documentId) => {
    const response = await api.delete(`/api/documents/${documentId}`);
    return response.data;
  },
};

// Chat API calls
export const chatAPI = {
  sendMessage: async (message, sessionId = null, documentId = null) => {
    const response = await api.post('/api/chat', {
      message,
      session_id: sessionId,
      document_id: documentId,
    });
    return response.data;
  },

  getSessions: async () => {
    const response = await api.get('/api/chat/sessions');
    return response.data;
  },

  getHistory: async (sessionId) => {
    const response = await api.get(`/api/chat/history/${sessionId}`);
    return response.data;
  },
};

// Admin API calls
export const adminAPI = {
  getAllUsers: async () => {
    const adminToken = localStorage.getItem('adminToken');
    const response = await api.get('/api/admin/users', {
      headers: {
        'X-Admin-Token': adminToken || 'admin_authenticated',
      },
    });
    return response.data;
  },

  getAllDocuments: async () => {
    const adminToken = localStorage.getItem('adminToken');
    const response = await api.get('/api/admin/documents', {
      headers: {
        'X-Admin-Token': adminToken || 'admin_authenticated',
      },
    });
    return response.data;
  },
};

export default api;
